import { CommonModule, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ModalService } from '../../../services/modal.service';
import { Observable, forkJoin, fromEvent, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, share } from 'rxjs/operators';
import { ItemsPerPageComponent } from '../../../components/itemsPerPage.component';
import { Campaign } from '../../../models/campaign.model';
import { Organisation } from '../../../models/organisation.model';
import { Page } from '../../../models/page.model';
import { Parcel, ToolStatus } from '../../../models/parcel.model';
import { ZSNCampaign, ZSNOrderItem, ZSNParcel } from '../../../models/zsn.model';
import { CampaignYearPipe } from '../../../pipes/campaignYear.pipe';
import { CapitalizePipe } from '../../../pipes/capitalize.pipe';
import { ExtractAttributePipe } from '../../../pipes/extractAttribute.pipe';
import { HectareConverterPipe } from '../../../pipes/hectareConverter.pipe';
import { ToLabelPipe } from '../../../pipes/toLabel.pipe';
import { ZSNParcelAgroStatusPipe, ZSNParcelOrderStatusPipe } from '../../../pipes/zsnParcelStatus.pipe';
import { FarmerAuthorizationsService } from '../../../services/farmer-authorizations.service';
import { KeycloakService } from '../../../services/keycloak.service';
import { OrganisationService } from '../../../services/organisation.service';
import { ParcelService } from '../../../services/parcel.service';
import { ZSNListsService } from '../../../services/zsn-lists.service';
import { ZSNParcelFilter, ZSNService } from '../../../services/zsn.service';
import { Tooltip } from '../../../utils/Tooltip';
import { TOOLTIP_TEXTS } from '../../../utils/tooltip-texts';
import { TooltipContent } from '../../../utils/TooltipContent';
import { showErrorMessage, toBoolean, toNull, toNumber } from '../../../utils/utils';
import { ParcelDetailsDialogComponent, ParcelDetailsDialogContext } from '../../parcel/parcel-details-dialog.component';
import { ZSNUtils } from './zsn.utils';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

@Component({
  standalone: true,
  selector: 'ar-Zsn-parcels-cmp',
  templateUrl: 'parcels.component.html',
  styleUrls: ['parcels.component.scss'],
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    CommonModule,
    FormsModule,
    Tooltip,
    TooltipContent,
    ItemsPerPageComponent,
    DatePipe,
    HectareConverterPipe,
    ToLabelPipe,
    CapitalizePipe,
    ExtractAttributePipe,
    CampaignYearPipe,
    ZSNParcelOrderStatusPipe,
    ZSNParcelAgroStatusPipe,
    TranslatePipe,
    PaginationModule,
    TooltipModule,
  ],
  providers: [ZSNService, ParcelService, OrganisationService],
})
export class ZSNParcelComponent implements OnInit, AfterViewInit {
  error: any;
  @ViewChild('termPrefixFilterParcel') termPrefixFilterParcel: ElementRef;
  @ViewChild('termPrefixFilterExploitation') termPrefixFilterExploitation: ElementRef;
  @ViewChild('termPrefixFilterIdParcel') termPrefixFilterIdParcel: ElementRef;
  @ViewChild('termCampaignFilter') termCampaignFilter: ElementRef;
  @ViewChild('termCompletionStatusFilter') termCompletionStatusFilter: ElementRef;
  @ViewChild('termOrderStatusFilter') termOrderStatusFilter: ElementRef;
  @ViewChild('termOrganisationFilter') termOrganisationFilter: ElementRef;
  @ViewChild('allFilters', { static: true }) allFilters: ElementRef;

  // gestion des checkboxes
  @ViewChildren('parcelCheckbox') parcelCheckboxes: any;
  @ViewChild('checkboxAll') checkboxAll: any;
  lengthAllParcels: number;
  checkedParcelIds = new Set<number>();
  openedCampaignsDateChecks = new Map<number, any>();

  prefixFilterParcel: string = '';
  prefixFilterExploitation: string = '';
  prefixFilterIdParcel: number = null;
  campaignFilter: number = null;
  completionStatusFilter: boolean = null;
  orderStatusFilter: boolean = null;
  organisationFilter: number = null;

  filterToggleIcon = 'fa fa-plus';
  showAllFilters = false;

  parcels: ZSNParcel[] = [];
  page: number = 1;
  itemsPerPage: number = 25;
  maxSize: number = 5;
  numPages: number = 1;
  length: number = 0;

  campaigns: ZSNCampaign[];

  showRef: boolean;
  showResultStatus: boolean;

  showOrganisationFilter = false;
  organisations: Organisation[];

  tooltipTexts = TOOLTIP_TEXTS;

  // Loaded async from block 6 (C2027-196); defaults to true so the button does not flicker.
  canCreateOrder = true;

  // static pour garder entre affichages de la liste
  static filterToApply: ZSNParcelFilter;
  static allFiltersToShow: boolean;

  constructor(
    private zsnService: ZSNService,
    private bleListsService: ZSNListsService,
    private parcelService: ParcelService,
    private organisationService: OrganisationService,
    private router: Router,
    private modal: ModalService,
    private cdr: ChangeDetectorRef,
    private farmerAuthorizationsService: FarmerAuthorizationsService,
  ) {
    this.showRef = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    this.showResultStatus =
      KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN') || KeycloakService.auth.authz.hasRealmRole('ROLE_SECTOR');
    this.farmerAuthorizationsService.can('createOrder').subscribe((allowed) => (this.canCreateOrder = allowed));
  }

  getParcelsPaginated(pageIndex: number, sortColumnName: string, sortColumnOrder: string, filter: ZSNParcelFilter) {
    this.zsnService
      .getPageParcels(pageIndex, this.itemsPerPage, sortColumnName, sortColumnOrder, filter)
      .subscribe((page) => this.handlePage(page));
  }

  handlePage(page: Page<ZSNParcel>) {
    this.parcels = page.content;
    this.numPages = page.totalPages;
    this.length = page.totalElements;

    if (this.showResultStatus) {
      this.getParcelStatuses();
    }
  }

  ngOnInit() {
    const obs1 = this.zsnService.getCampaigns().pipe(share());
    obs1.subscribe((campaigns: any) => {
      this.handleCampaigns(campaigns);
    });
    const obs3 = this.getOrganisations();

    forkJoin([obs1, obs3]).subscribe((data: Array<any>) => {
      if (!ZSNParcelComponent.filterToApply) {
        this.campaignFilter = this.campaigns[0].id;
        ZSNParcelComponent.filterToApply = this.buildFilter();
      }
      this.updateUIWithFilters();

      this.onChangeTable();
    });
  }

  updateUIWithFilters() {
    if (ZSNParcelComponent.filterToApply) {
      if (this.showRef) {
        this.termPrefixFilterExploitation.nativeElement.value = this.prefixFilterExploitation;
        this.prefixFilterIdParcel = ZSNParcelComponent.filterToApply.prefixIdParcel;
      }
      this.prefixFilterParcel = ZSNParcelComponent.filterToApply.prefixParcel;
      this.termPrefixFilterParcel.nativeElement.value = this.prefixFilterParcel;
      this.prefixFilterExploitation = ZSNParcelComponent.filterToApply.prefixExploitation;
      this.termPrefixFilterIdParcel.nativeElement.value = this.prefixFilterIdParcel;
      this.organisationFilter = toNumber(ZSNParcelComponent.filterToApply.organisationId);
      this.campaignFilter = toNumber(ZSNParcelComponent.filterToApply.campaignId);
      this.completionStatusFilter = toBoolean(ZSNParcelComponent.filterToApply.completionStatus);
      this.orderStatusFilter = toBoolean(ZSNParcelComponent.filterToApply.orderStatus);
    }

    if (ZSNParcelComponent.allFiltersToShow) {
      this.toggleAllFilters();
    }
  }

  ngAfterViewInit() {
    // TODO ameliorer => pas de debounce quand clearFilter a ete appele
    const observables = new Array<Observable<{}>>();
    const obs1 = fromEvent(this.termPrefixFilterParcel.nativeElement, 'keyup').pipe(
      debounceTime(300),
      distinctUntilChanged(),
    );
    const obs2 = fromEvent(this.termPrefixFilterExploitation.nativeElement, 'keyup').pipe(
      debounceTime(300),
      distinctUntilChanged(),
    );
    const obs3 = fromEvent(this.termOrderStatusFilter.nativeElement, 'change');
    const obs4 = fromEvent(this.termCompletionStatusFilter.nativeElement, 'change');
    const obs5 = fromEvent(this.termCampaignFilter.nativeElement, 'change');
    observables.push(obs1, obs2, obs3, obs4, obs5);

    if (this.showOrganisationFilter) {
      const obs6 = fromEvent(this.termOrganisationFilter.nativeElement, 'change');
      observables.push(obs6);
    }

    if (this.showRef) {
      const obs7 = fromEvent(this.termPrefixFilterIdParcel.nativeElement, 'keyup').pipe(
        debounceTime(300),
        distinctUntilChanged(),
      );
      observables.push(obs7);
      obs7.subscribe((term) => (this.prefixFilterIdParcel = <number>this.termPrefixFilterIdParcel.nativeElement.value));
    }

    obs1.subscribe((term) => (this.prefixFilterParcel = <string>this.termPrefixFilterParcel.nativeElement.value));
    obs2.subscribe(
      (term) => (this.prefixFilterExploitation = <string>this.termPrefixFilterExploitation.nativeElement.value),
    );
    merge(...observables)
      .pipe(debounceTime(10))
      .subscribe((term: any) => {
        this.updateFilters();
      });
  }

  getOrganisations(): Observable<Organisation[]> {
    const isAdmin = KeycloakService.auth.authz.hasRealmRole('ROLE_ADMIN');
    const isNegoce = KeycloakService.auth.authz.hasRealmRole('ROLE_NEGOCE');
    if (isAdmin || isNegoce) {
      this.showOrganisationFilter = true;
      const obs = this.organisationService.getOrganisations().pipe(share());
      obs.subscribe((orgs) => {
        this.organisations = orgs.sort((a, b) => a.name.localeCompare(b.name));
      });
      return obs;
    } else {
      return of([]);
    }
  }

  clearFilters() {
    this.prefixFilterParcel = null;
    this.prefixFilterExploitation = null;
    this.completionStatusFilter = null;
    this.prefixFilterIdParcel = null;
    this.orderStatusFilter = null;
    this.campaignFilter = null;
    this.organisationFilter = null;
    this.termPrefixFilterParcel.nativeElement.value = null;
    this.termPrefixFilterExploitation.nativeElement.value = null;
    this.termCampaignFilter.nativeElement.value = null;
    this.termCompletionStatusFilter.nativeElement.value = null;
    this.termOrderStatusFilter.nativeElement.value = null;
    if (this.prefixFilterIdParcel) {
      this.termPrefixFilterIdParcel.nativeElement.value = null;
    }
    if (this.showOrganisationFilter) {
      this.termOrganisationFilter.nativeElement.value = null;
    }
    this.updateFilters();
  }

  updateFilters() {
    ZSNParcelComponent.filterToApply = this.buildFilter();

    // reset la page à la première et update la table
    // FIXME faire mieux : suspendre le traitement des changements ?
    if (this.page === 1) {
      this.onChangeTable();
    } else {
      // le biding (pagechanged) du contrôle de pagination va appeler onChangeTable
      this.page = 1;
    }

    this.clearCheckboxes();
  }

  onChangeTable(page: any = { page: this.page, itemsPerPage: this.itemsPerPage }): any {
    const pageNumber = page.page;
    this.getParcelsPaginated(pageNumber, null, null, ZSNParcelComponent.filterToApply);
  }

  onChangeItemsPerPage(newValue: number) {
    const page = this.page;
    this.cdr.detectChanges();
    if (page === this.page) {
      this.onChangeTable();
    }
  }

  buildFilter(): ZSNParcelFilter {
    // binding Angular2 écrit en chaine... On fait la conversion en boolean
    const compFilter: string = '' + this.completionStatusFilter;
    const stFilter: string = '' + this.orderStatusFilter;
    const filter = {
      prefixParcel: toNull(this.prefixFilterParcel),
      prefixExploitation: toNull(this.prefixFilterExploitation),
      farmId: <any>null,
      campaignId: toNull(this.campaignFilter),
      prefixIdParcel: toNumber(this.prefixFilterIdParcel),
      organisationId: toNull(this.organisationFilter),
      completionStatus: compFilter === 'true' ? true : compFilter === 'false' ? false : null,
      orderStatus: stFilter === 'true' ? true : stFilter === 'false' ? false : null,
    };
    return filter;
  }

  addOrderItem(parcel: ZSNParcel) {
    this.router.navigate(['/orders', 'zsn', 'parcels', 'new', parcel.id]);
  }

  deleteOrderItem(orderItem: ZSNOrderItem, event: any) {
    event.stopPropagation();
    // FIXME traiter les erreurs
    this.zsnService.deleteOrderItem(orderItem).subscribe(
      (res) => {
        this.onChangeTable();
      },
      (error) => this.handleError(error, 'Impossible de supprimer les détails Densité de semis'),
    );
  }

  editOrderItem(orderItem: ZSNOrderItem, event: any) {
    this.router.navigate(['/orders', 'zsn', 'parcels', 'edit', orderItem.id]);
  }

  getParcelStatuses() {
    //	TODO handle errors
    if (!this.parcels || this.parcels.length === 0) {
      return;
    }

    const parcelIndex = new Map<number, Parcel>();
    for (const parcel of this.parcels) {
      parcelIndex.set(parcel.id, parcel);
    }
    this.parcelService.getParcelStatuses(this.parcels.map((x) => x.id)).subscribe((x) => {
      for (const p of x) {
        const parcel = parcelIndex.get(p.parcelId);
        const tp: any = parcel;
        // uniquement utilisé dans cet écran
        // récuperer liste
        const results: Array<string> = [];
        if (p.toolStatuses && (<any>p.toolStatuses)['ZSN']) {
          const toolStatus = <ToolStatus>(<any>p.toolStatuses)['ZSN'];
          if (toolStatus.resultIds) {
            for (const resultType in <any>toolStatus.resultIds) {
              results.push(resultType);
            }
          }
        }

        if (results.length > 0) {
          tp['results'] = results.map((x) => this.toText(x)).join(', ');
        } else {
          tp['results'] = 'Aucun fichier';
        }
      }
    });
  }

  toText(x: string) {
    if (x === 'ZSN_REPORT') {
      return 'Restitution';
    } else if (x === 'ZSN_MODULATION') {
      return 'Modulation';
    }
    return '';
  }

  getStatusColor(parcel: ZSNParcel) {
    if (!parcel.orderItem) {
      return 'red';
    } else {
      if (!parcel.orderItem.completed) {
        return 'orange';
      } else {
        if (!parcel.orderItem.order) {
          return 'green';
        } else {
          return 'blue';
        }
      }
    }
  }

  handleError(error: any, msg: string) {
    if (error.status === 401) {
      // FIXME message selon le contexte
      showErrorMessage(this.modal, "Vous n'êtes pas autorisé à faire cette action");
    } else {
      console.error(error);
      showErrorMessage(this.modal, msg);
    }
  }

  viewParcel(parcelId: number, event: any) {
    const context = new ParcelDetailsDialogContext();
    context.showClose = true;
    context.isBlocking = false;
    context.size = 'lg';
    context.parcelId = parcelId;
    context.showEdit = true;
    this.modal.open(ParcelDetailsDialogComponent, context);
  }

  isCampaignCurrentlyOpen(campaign: ZSNCampaign): boolean {
    return ZSNUtils.isCampaignCurrentlyOpen(campaign);
  }

  addOrder() {
    if (this.checkedParcelIds.size === 0) {
      this.router.navigate(['/orders/zsn/orders/new']);
    } else {
      const parcelIds = Array.from(this.checkedParcelIds.values()).join('-');
      this.router.navigate(['/orders/zsn/orders/new/' + parcelIds]);
    }
  }

  batchEdit() {
    if (this.checkedParcelIds.size > 1) {
      const parcelIds = Array.from(this.checkedParcelIds.values()).join('-');
      this.router.navigate(['/orders/zsn/parcels/batch/' + parcelIds]);
    } else if (this.checkedParcelIds.size === 1) {
      // on redirige vers la page non batch (sans les checkbox par donnée)
      const parcelId = Array.from(this.checkedParcelIds.values())[0];
      this.router.navigate(['/orders/zsn/parcels/edit/' + parcelId]);
    }
  }

  addParcel() {
    this.router.navigate(['/parcels', 'new']);
  }

  handleCampaigns(campaigns: ZSNCampaign[]) {
    this.campaigns = campaigns.sort(Campaign.compare).reverse();
    campaigns = campaigns.filter((x) => ZSNUtils.isCampaignCurrentlyOpen(x));
    for (const campaign of campaigns) {
      const dateChecks = {
        campaign: campaign,
        isCurrentlyOpen: ZSNUtils.isCampaignCurrentlyOpen(campaign),
      };
      this.openedCampaignsDateChecks.set(campaign.id, dateChecks);
    }
  }

  isParcelChecked(parcel: ZSNParcel): boolean {
    return this.checkedParcelIds.has(parcel.id);
  }

  isParcelDisabled(parcel: ZSNParcel): boolean {
    const c = this.openedCampaignsDateChecks.get(parcel.campaign.id);
    if (c) {
      // disabled si : n'a pas encore d'OrderItem ou a déjà une commande
      return !parcel.orderItem || !!parcel.orderItem.order;
    } else {
      return true;
    }
  }

  onCheckboxClicked(index: number) {
    const checkboxes = this.parcelCheckboxes.toArray().map((x: any) => x.nativeElement);
    const parcel = this.parcels[index];
    const checkbox = checkboxes[index];

    if (checkbox.checked) {
      this.checkedParcelIds.add(parcel.id);
    } else {
      this.checkedParcelIds.delete(parcel.id);
    }
  }

  onCheckboxAllClicked() {
    if (this.checkboxAll.nativeElement.checked) {
      const filter = this.buildFilter();
      this.zsnService.getAllParcelIds(filter).subscribe(
        (x) => {
          this.lengthAllParcels = x.length;

          for (const idx of x) {
            this.checkedParcelIds.add(idx);
          }
          // pas de modèle : on coche à la main
          const checkboxes = this.parcelCheckboxes.toArray().map((x: any) => x.nativeElement);
          for (let i = 0; i < checkboxes.length; i++) {
            const checkbox = checkboxes[i];
            checkbox.checked = true;
          }
        },
        (error) => {
          this.handleError(error, 'Impossible de sélectionner les parcelles');
        },
      );
    } else {
      this.clearCheckboxes();
    }
  }

  clearCheckboxes() {
    this.lengthAllParcels = undefined;
    this.checkboxAll.nativeElement.checked = false;
    // decoche tout
    this.checkedParcelIds.clear();
    // pas de modèle : on décoche à la main
    const checkboxes = this.parcelCheckboxes.toArray().map((x: any) => x.nativeElement);
    for (const checkbox of checkboxes) {
      checkbox.checked = false;
    }
  }

  isCheckboxAllChecked(): boolean {
    return this.lengthAllParcels === this.checkedParcelIds.size;
  }

  toggleAllFilters() {
    if (this.showAllFilters) {
      this.showAllFilters = false;
      this.filterToggleIcon = 'fa fa-plus';
      this.allFilters.nativeElement.style.display = 'none';
    } else {
      this.showAllFilters = true;
      this.filterToggleIcon = 'fa fa-minus';
      this.allFilters.nativeElement.style.display = 'block';
    }

    ZSNParcelComponent.allFiltersToShow = this.showAllFilters;
  }
}
